import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TbChevronRight } from "react-icons/tb";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import {
  CustomButton,
  CustomMRTTable,
  CustomLoader,
} from "../../../components";
import AxiosInstance from "../../../services/Api";
import styles from "../plans-pricing/PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/addon-configuration";
const ADDON_LIST_API = "/saas/list/add-ons";

// ── Status mapping: API returns numeric status per module ─────────────────────
// 1 = Active (green), 2 = Upcoming (blue), 0 = Inactive (red)
// Slab-level status derived from modules inside each slab group
const STATUS_BADGE = {
  Active: { bg: "#00A63E", color: "#fff" },
  Upcoming: { bg: "#039DD9", color: "#fff" },
  Inactive: { bg: "#E7000B", color: "#fff" },
};

const StatusBadge = ({ label }) => {
  const style = STATUS_BADGE[label] || { bg: "#9e9e9e", color: "#fff" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 18px",
        borderRadius: "6px",
        fontSize: "13px",
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
        whiteSpace: "nowrap",
        cursor: "default",
        userSelect: "none",
      }}
    >
      {label}
    </span>
  );
};

const isSuccess = (response) => response?.data?.status === true;

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN")}`;

// The API sends amounts as strings ("100"), and MRT's auto-detected sort then
// compares them as text — "100" < "200" < "50". Store them as numbers so the
// range columns sort numerically.
// Missing amounts stay `undefined` — that matches a range key the module has no
// row for, and lets sortUndefined push them to the bottom.
const toAmount = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const amount = Number(value);
  return Number.isNaN(amount) ? undefined : amount;
};

// created_date arrives as DD/MM/YY. Sorting that as text puts 16/09/26 before
// 27/08/26, so sort on a parsed timestamp instead. Returns null when the value
// isn't a date we recognise (e.g. the "—" placeholder).
const parseCreatedDate = (value) => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(
    String(value || "").trim(),
  );
  if (!match) return null;
  const [, day, month, year] = match;
  const fullYear = year.length === 2 ? 2000 + Number(year) : Number(year);
  return new Date(fullYear, Number(month) - 1, Number(day)).getTime();
};

// Nulls sort last in both directions so real dates stay grouped together.
const sortByCreatedDate = (rowA, rowB) => {
  const a = parseCreatedDate(rowA.original.createdDate);
  const b = parseCreatedDate(rowB.original.createdDate);
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

const AddonConfigList = () => {
  const navigate = useNavigate();

  const [slabSections, setSlabSections] = useState([]);
  const [openSections, setOpenSections] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Fetch list ────────────────────────────────────────────────────────────
  const fetchAddonList = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AxiosInstance.post(ADDON_LIST_API, {
        module_id: "",
        status: "",
        page: "",
        limit: "",
      });

      if (!isSuccess(response)) {
        throw new Error(
          response?.data?.message || "Failed to fetch add-on pricing",
        );
      }

      // API returns array of slab objects, each with modules[]
      const raw = response.data.data || [];

      const sections = raw.map((slab) => {
        // Build accordion header label: "range1/range2/range3/range4"
        const rangeLabel = [slab.range1, slab.range2, slab.range3, slab.range4]
          .filter(Boolean)
          .join("/");

        // Derive slab-level status from modules:
        // If any module is Active → Active; else if any Upcoming → Upcoming; else Inactive
        const moduleStatuses = (slab.modules || []).map((m) => m.status_label);
        let slabStatus = "Inactive";
        if (moduleStatuses.includes("Active")) slabStatus = "Active";
        else if (moduleStatuses.includes("Upcoming")) slabStatus = "Upcoming";

        // Build range column headers from slab ranges
        const rangeHeaders = [
          { key: "range1", limit: slab.range1 },
          { key: "range2", limit: slab.range2 },
          { key: "range3", limit: slab.range3 },
          { key: "range4", limit: slab.range4 },
        ].filter((r) => r.limit != null);

        // Build table rows — one per module
        const rows = (slab.modules || []).map((m) => {
          const rangeAmounts = {};
          (m.ranges || [])
            .slice()
            .sort((a, b) => a.range_no - b.range_no)
            .forEach((r) => {
              rangeAmounts[`range${r.range_no}`] = toAmount(r.amount);
            });
          return {
            moduleId: m.module_id,
            moduleName: m.module_name,
            pricingId: m.pricing_id,
            createdDate: m.created_date || "—",
            canEdit: m.can_edit,
            statusLabel: m.status_label,
            ...rangeAmounts,
          };
        });

        return {
          slabId: slab.slab_configuration_id,
          rangeLabel,
          slabStatus,
          rangeHeaders,
          rows,
        };
      });

      setSlabSections(sections);
      // Default: first section open
      setOpenSections((prev) =>
        Object.keys(prev).length
          ? prev
          : sections.reduce(
              (acc, s, i) => ({ ...acc, [s.slabId]: i === 0 }),
              {},
            ),
      );
    } catch (err) {
      console.error("Error fetching add-on pricing:", err);
      setError(
        err.message || "An error occurred while fetching add-on pricing",
      );
      setSlabSections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAddonList();
  }, [fetchAddonList]);

  const toggleSection = (slabId) => {
    setOpenSections((prev) => ({ ...prev, [slabId]: !prev[slabId] }));
  };

  const handleEditClick = (slabId) => {
    navigate(`${LIST_PATH}/create`, { state: { slabId } });
  };

  // ── Build columns per slab (rangeHeaders differ per slab) ────────────────
  const buildColumns = (rangeHeaders) => [
    {
      accessorKey: "moduleName",
      header: "Add-ons Features",
      size: 200,
      enableSorting: true,
      enableColumnFilter: false,
    },
    ...rangeHeaders.map((rh) => ({
      accessorKey: rh.key,
      header: `Up to ${Number(rh.limit).toLocaleString()} users`,
      size: 160,
      enableColumnFilter: false,
      sortingFn: "basic", // numeric compare, not the auto-detected text compare
      sortUndefined: "last",
      Cell: ({ cell }) => {
        const val = cell.getValue();
        if (val == null || val === "" || Number(val) === 0)
          return <span style={{ color: "#9ca3af" }}>-</span>;
        return <span>{formatCurrency(val)}</span>;
      },
    })),
    {
      accessorKey: "createdDate",
      header: "Created Date",
      size: 130,
      enableColumnFilter: false,
      sortingFn: sortByCreatedDate,
      Cell: ({ cell }) => cell.getValue() || "—",
    },
  ];

  if (loading) {
    return (
      <div>
        <div className={styles.loadingContainer}>
          <CustomLoader text="Loading add-on pricing..." />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Page header ── */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Add-on Configuration</h1>
        </div>
      </div>

      {error && <p style={{ color: "#E23B3B", margin: "12px 0" }}>{error}</p>}

      <div className={styles.accordionCard}>
        <h2 className={styles.accordionCardTitle}>Add-ons Pricing</h2>

        {slabSections.length === 0 && !loading && (
          <p style={{ color: "#6B7280", padding: "16px" }}>
            No add-on pricing configured yet.
          </p>
        )}

        {slabSections.map((section) => {
          const isOpen = Boolean(openSections[section.slabId]);
          const columns = buildColumns(section.rangeHeaders);

          return (
            <div key={section.slabId} className={styles.accordionSection}>
              {/* ── Accordion header row ── */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  cursor: "pointer",
                  background: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
                onClick={() => toggleSection(section.slabId)}
              >
                {/* Left: chevron + range label */}
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      transition: "transform 0.2s",
                      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                      color: "#374151",
                    }}
                  >
                    <TbChevronRight size={18} />
                  </span>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "15px",
                      color: "#1A1A2E",
                    }}
                  >
                    {section.rangeLabel}
                  </span>
                </div>

                {/* Right: status badge + edit icon */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "default",
                  }}
                  onClick={(e) => e.stopPropagation()} // don't toggle on badge/icon click
                >
                  <StatusBadge label={section.slabStatus} />
                  <Tooltip title="Edit" arrow>
                    <IconButton
                      size="small"
                      className={styles.iconActionBtn}
                      style={{
                        background: "#F5821F",
                        color: "#fff",
                        borderRadius: "6px",
                        padding: "6px",
                      }}
                      onClick={() => handleEditClick(section.slabId)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>

              {/* ── Accordion body: table ── */}
              {isOpen && (
                <div className={styles.accordionBody}>
                  <CustomMRTTable
                    columns={columns}
                    data={section.rows}
                    manualPagination={false}
                    manualFiltering={false}
                    enablePagination={section.rows.length > 10}
                    enableSorting
                    enableSearch={false}
                    enableColumnFilters={false}
                    enableColumnVisibility={false}
                    enableDensityToggle={false}
                    enableFullScreenToggle={false}
                    enableHiding={false}
                    enableColumnActions={false}
                    enableStickyHeader
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AddonConfigList;
